/* eslint-disable @typescript-eslint/no-explicit-any */
import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Toast } from 'sharedUi/Toast';
import { Tag } from 'primereact/tag';
import { Button } from 'sharedUi/Button';
import { Dropdown } from 'sharedUi/Dropdown';
import { RefreshButton } from 'sharedUi/RefreshButton';
import { useSecureNavigation } from '../../hooks/useSecureNavigation';
import { useDebounce } from '../../hooks/useDebounce';
import { recipientMastersService, type RecipientMaster } from '../../services/recipient-masters.service';
import { totalOf, NEWEST_FIRST, sortParams, type SortState } from '../../services/notification-engine.types';
import { formatTimestamp } from 'sharedUi/dateUtils';
import { extractErrorMessage } from '../../utils/error-handler';
import PermissionGuard from '../../components/guards/PermissionGuard';
import { JOB_NAMES } from '../../constants/jobNames';
import PageHeader from '../../components/common/page-header';
import SaasDataTable, { type SaasColumn } from '../../components/common/saas-data-table';
import { ProjectCell } from '../../components/common/project-cell';
import { useAccessibleProjects } from '../../hooks/useProjectScope';
import ProjectFilter from '../../components/common/project-filter';
import ShowCoreToggle from '../../components/common/show-core-toggle';
import Icon from '../../components/common/icon';
import DeleteConfirmDialog from '../../components/dialogs/delete-confirm-dialog';
import { useCanCreate, useCanUpdate, useCanDelete } from '../../hooks/usePermissions';

/** FUNCTION runs a chain and AI_AGENT is not built yet, so both stand out. */
const typeSeverity = (t?: string | null) =>
  t === 'FUNCTION' ? 'info' : t === 'AI_AGENT' ? 'warning' : 'secondary';

/** The recipient kinds, for the Type filter. */
const TYPE_OPTIONS = [
  { label: 'List', value: 'LIST' },
  { label: 'Function', value: 'FUNCTION' },
  { label: 'AI agent', value: 'AI_AGENT' },
  { label: 'User', value: 'USER' },
  { label: 'Personnel', value: 'PERSONNEL' },
  { label: 'External', value: 'EXTERNAL' },
];

const RecipientMasterList = ({ embedded = false }: { embedded?: boolean }) => {
  const { navigateToCreate, navigateToView, navigateToEdit } = useSecureNavigation({
    entity: 'recipient', basePath: '/recipient-masters',
  });
  const toast = useRef<Toast>(null);
  const canCreate = useCanCreate(JOB_NAMES.RECIPIENT_MASTER);
  const canUpdate = useCanUpdate(JOB_NAMES.RECIPIENT_MASTER);
  const canDelete = useCanDelete(JOB_NAMES.RECIPIENT_MASTER);

  // Which project each row belongs to. The COLUMN always shows — "this project"
  // versus the shared "Global" rows is worth seeing even to someone with one
  // project — while the filter beside it only appears when there is a choice.
  const { projects, canScopeByProject } = useAccessibleProjects();

  const [rows, setRows] = useState<RecipientMaster[]>([]);
  const [loading, setLoading] = useState(true);
  const [first, setFirst] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [total, setTotal] = useState(0);
  // Server-side sort: the table asks, the API orders. Sorting in the browser
  // would only order the rows already fetched, which is not what a sort control
  // appears to promise. Newest first until the viewer picks a column.
  const [sort, setSort] = useState<SortState>(NEWEST_FIRST);
  const [toDelete, setToDelete] = useState<RecipientMaster | null>(null);

  // Filters. Debounced search over code + description; the type dropdown applies
  // immediately. Both are server-side, narrowing the whole table.
  const [search, setSearch] = useState('');
  const [recipientType, setRecipientType] = useState<string | null>(null);
  const [appId, setAppId] = useState<string | null>(null);
  const [showCore, setShowCore] = useState(false);
  const debouncedSearch = useDebounce(search, 400);

  const showError = useCallback((m: string) => toast.current?.show({ severity: 'error', summary: 'Error', detail: m, life: 8000 }), []);
  const showOk = useCallback((m: string) => toast.current?.show({ severity: 'success', summary: 'Success', detail: m, life: 3000 }), []);

  const fetchRows = useCallback(async (skip = 0, limit = pageSize, sorting: SortState = sort) => {
    try {
      setLoading(true);
      const resp = await recipientMastersService.list({
        skip, limit, ...sortParams(sorting),
        ...(debouncedSearch.trim() ? { search: debouncedSearch.trim() } : {}),
        ...(recipientType ? { recipientType } : {}),
        ...(appId ? { appId } : {}),
        include_global: showCore,
      });
      setRows(resp.data || []);
      setTotal(totalOf(resp));
    } catch (e) {
      showError(extractErrorMessage(e, 'Failed to load recipients'));
    } finally {
      setLoading(false);
    }
  }, [pageSize, sort, showError, debouncedSearch, recipientType, appId, showCore]);

  // First load, and again on any filter change — always from page one, so a
  // narrowed result cannot strand the viewer on a page that no longer exists.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { setFirst(0); fetchRows(0, pageSize); }, [debouncedSearch, recipientType, appId, showCore]);

  const handlePage = (e: any) => { setFirst(e.first); setPageSize(e.rows); fetchRows(e.first, e.rows); };

  const handleSort = (e: any) => {
    const next: SortState = { field: e.sortField || null, order: e.sortOrder || null };
    setSort(next);
    setFirst(0);
    fetchRows(0, pageSize, next);
  };

  const handleDelete = async () => {
    if (!toDelete) return;
    try {
      await recipientMastersService.softDelete(toDelete._id);
      setToDelete(null);
      showOk('Recipient deleted');
      fetchRows(first, pageSize);
    } catch (e) {
      showError(extractErrorMessage(e, 'Failed to delete recipient'));
    }
  };

  const columns: SaasColumn<RecipientMaster>[] = useMemo(() => [
    { field: 'recipientCode', header: 'Code', sortable: true, body: (r) => <span className="font-mono text-xs">{r.recipientCode}</span> },
    { field: 'recipientType', header: 'Type', sortable: true, body: (r) => <Tag value={r.recipientType} severity={typeSeverity(r.recipientType)} className="text-xs px-1.5 py-0.5" /> },
    {
      field: 'functions', header: 'Chain',
      body: (r) => <span className="text-xs">{(r.functions || []).length ? `${(r.functions || []).length} step(s)` : '-'}</span>,
    },
    { field: 'actions', header: 'Actions attached', body: (r) => <span className="text-xs">{(r.actions || []).length || '-'}</span> },
    {
      field: 'appId', header: 'Project', style: { minWidth: 150 },
      body: (r: RecipientMaster) => <ProjectCell appId={r.appId} projects={projects} />,
    },
    { field: 'createdAt', header: 'Created', sortable: true, body: (r) => <span className="text-xs text-gray-500 whitespace-nowrap">{r.createdAt ? formatTimestamp(r.createdAt) : '-'}</span> },
    {
      field: 'rowActions', header: 'Actions', align: 'center', hideable: false,
      body: (r) => (
        <div className="flex gap-1 justify-center">
          <button aria-label="View" className="p-2 rounded hover:bg-gray-100 dark:hover:bg-gray-700" onClick={() => navigateToView(r._id)} title="View">
            <Icon name="eye" size={17} className="text-green-600 dark:text-green-400" />
          </button>
          {canUpdate && <button aria-label="Edit" className="p-2 rounded hover:bg-gray-100 dark:hover:bg-gray-700" onClick={() => navigateToEdit(r._id)} title="Edit">
            <Icon name="pencil" size={17} className="text-blue-600 dark:text-blue-400" />
          </button>}
          {canDelete && <button aria-label="Delete" className="p-2 rounded hover:bg-gray-100 dark:hover:bg-gray-700" onClick={() => setToDelete(r)} title="Delete">
            <Icon name="trash" size={17} className="text-red-600 dark:text-red-400" />
          </button>}
        </div>
      ),
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [canUpdate, canDelete, canScopeByProject, projects]);

  return (
    <PermissionGuard jobName={JOB_NAMES.RECIPIENT_MASTER}>
      <Toast ref={toast} position="top-right" />
      <div className={embedded ? '' : 'p-6'} data-testid="SCR-RecipientMaster-List">
        {!embedded && (
          <PageHeader icon="pi pi-sitemap" title="Recipient Master" subtitle="A named audience — from a list, or worked out by a function chain" testId="RecipientMaster.Header" />
        )}
        <div className={embedded ? '' : 'bg-surface rounded-xl shadow-sm border border-line overflow-hidden'}>
          <SaasDataTable
            tableId="recipient-master" columns={columns} data={rows} loading={loading} dataKey="_id"
            emptyMessage="No recipients found" first={first} rows={pageSize} totalRecords={total}
            onPage={handlePage} rowsPerPageOptions={[5, 10, 25, 50]} sortField={sort.field || undefined} sortOrder={sort.order} onSort={handleSort} onFilter={() => {}} testId="RecipientMasterList"
            globalSearch={search} onGlobalSearchChange={setSearch} globalSearchPlaceholder="Search code or description..."
            toolbarStart={
              <div className="flex flex-wrap items-center gap-2">
                <ProjectFilter value={appId} onChange={setAppId} testId="RecipientMasterList.Filter.Project" />
                <Dropdown value={recipientType} options={TYPE_OPTIONS} onChange={(e: any) => setRecipientType(e.value || null)} placeholder="Type" showClear style={{ minWidth: '150px' }} data-testid="RecipientMasterList.Filter.Type" />
                <ShowCoreToggle checked={showCore} onChange={setShowCore} testId="RecipientMasterList.Toggle.ShowCore" />
              </div>
            }
            toolbarEnd={<>
              <span className="text-sm text-gray-400 whitespace-nowrap">{total} {total === 1 ? 'record' : 'records'}</span>
              <RefreshButton onClick={() => fetchRows(first, pageSize)} loading={loading} testId="RecipientMasterList.Refresh" />
              {canCreate && <Button label="Create Recipient" icon="pi pi-plus" onClick={() => navigateToCreate()} size="small" />}
            </>}
          />
        </div>
      </div>
      <DeleteConfirmDialog visible={!!toDelete} onCancel={() => setToDelete(null)} onDelete={handleDelete}
        message={`Delete recipient '${toDelete?.recipientCode}'?`} testId="RecipientMasterList.Delete" />
    </PermissionGuard>
  );
};

export default RecipientMasterList;
